import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ChevronDown, ChevronRight, Copy, MapPin, Check, X, Clock, Landmark } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending_transfer: "border-[#8B4530] text-[#D4936C]",
  pending_verification: "border-[#C8A96A] text-[#C8A96A]",
  paid: "border-[#7FB77E] text-[#7FB77E]",
  cancelled: "border-[#332D2B] text-[#756F6B]",
};

const STATUS_LABEL = {
  pending_transfer: "Awaiting Transfer",
  pending_verification: "Buyer Confirmed",
  paid: "Paid",
  cancelled: "Cancelled",
};

export default function AdminOrders() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [updating, setUpdating] = useState({});
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const r = await api.get("/orders");
      setItems(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Could not copy");
    }
  };

  const setStatus = async (o, status) => {
    setUpdating((u) => ({ ...u, [o.id]: true }));
    try {
      const r = await api.patch(`/orders/${o.id}/status`, { status });
      setItems((prev) => prev.map((it) => (it.id === o.id ? { ...it, ...r.data } : it)));
      toast.success(`Order ${o.order_reference} → ${STATUS_LABEL[status]}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setUpdating((u) => ({ ...u, [o.id]: false }));
    }
  };

  const filtered = filter === "all" ? items : items.filter((o) => o.status === filter);
  const counts = items.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  const filterTabs = [
    { key: "all", label: `All (${items.length})` },
    { key: "pending_transfer", label: `Awaiting (${counts.pending_transfer || 0})` },
    { key: "pending_verification", label: `To Verify (${counts.pending_verification || 0})` },
    { key: "paid", label: `Paid (${counts.paid || 0})` },
    { key: "cancelled", label: `Cancelled (${counts.cancelled || 0})` },
  ];

  return (
    <div data-testid="admin-orders">
      <div className="font-mono-retro text-xs tracking-[0.4em] text-[#C8A96A] uppercase">Match Receipts</div>
      <h1 className="font-display text-4xl text-[#F2EBDD] mt-1">Orders</h1>
      <p className="text-[#A8A39D] mt-2 text-sm">
        Bank transfers are manual — once funds land in your account, mark the order as <span className="text-[#7FB77E]">Paid</span>.
        Stock and raffle counts are only decremented on that transition.
      </p>

      {/* Filter tabs */}
      <div className="mt-6 flex flex-wrap gap-2">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            data-testid={`filter-${tab.key}`}
            onClick={() => setFilter(tab.key)}
            className={`font-mono-retro text-[10px] tracking-[0.25em] uppercase border px-3 py-2 transition-colors ${
              filter === tab.key
                ? "border-[#C8A96A] text-[#C8A96A] bg-[#1C1816]"
                : "border-[#332D2B] text-[#A8A39D] hover:border-[#A8A39D]"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mt-6 border border-[#332D2B] bg-[#1C1816] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-[#332D2B]">
            <tr className="text-left font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">
              <th className="p-4 w-8"></th>
              <th className="p-4">Reference</th>
              <th className="p-4">When</th>
              <th className="p-4">Item</th>
              <th className="p-4">Size</th>
              <th className="p-4">Customer</th>
              <th className="p-4">Ship To</th>
              <th className="p-4">Qty</th>
              <th className="p-4">Amount</th>
              <th className="p-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="p-10 text-center text-[#A8A39D]">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10} className="p-10 text-center text-[#A8A39D]">No orders in this view.</td></tr>
            ) : filtered.map((o) => {
              const meta = o.metadata || {};
              const isOpen = !!expanded[o.id];
              const hasShip = o.item_type === "jersey" && o.shipping_address;
              const isUpdating = !!updating[o.id];
              return (
                <React.Fragment key={o.id}>
                  <tr
                    className="border-b border-[#332D2B] hover:bg-[#2A2320] cursor-pointer"
                    onClick={() => toggle(o.id)}
                    data-testid={`order-row-${o.id}`}
                  >
                    <td className="p-4 text-[#756F6B]">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                    <td className="p-4">
                      <div className="font-display text-[#C8A96A] tracking-[0.1em]" data-testid={`order-ref-${o.id}`}>{o.order_reference || "—"}</div>
                    </td>
                    <td className="p-4 text-[#A8A39D] text-xs whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</td>
                    <td className="p-4 text-[#F2EBDD]">{o.item_name}</td>
                    <td className="p-4 text-[#A8A39D] font-mono-retro text-xs tracking-[0.2em] uppercase">{o.size || "—"}</td>
                    <td className="p-4 text-[#F2EBDD]">
                      <div>{o.customer_name}</div>
                      <div className="text-xs text-[#A8A39D]">{o.customer_email}</div>
                    </td>
                    <td className="p-4 max-w-[240px]">
                      {o.shipping_address ? (
                        <div className="text-[#F2EBDD] text-xs leading-snug whitespace-normal break-words" data-testid={`ship-inline-${o.id}`}>
                          {o.shipping_address}
                        </div>
                      ) : (
                        <span className="text-[#756F6B] text-xs italic">
                          {o.item_type === "raffle_ticket" ? "Raffle — no ship" : "—"}
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-[#A8A39D]">{o.quantity}</td>
                    <td className="p-4 text-[#C8A96A]">£{Number(o.amount || 0).toFixed(2)}</td>
                    <td className="p-4">
                      <span className={`font-mono-retro text-[10px] tracking-[0.25em] uppercase border px-2 py-1 whitespace-nowrap ${STATUS_STYLES[o.status] || "border-[#332D2B] text-[#A8A39D]"}`}>
                        {STATUS_LABEL[o.status] || o.status}
                      </span>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-[#0F0C0B] border-b border-[#332D2B]">
                      <td colSpan={10} className="p-6">
                        {/* Status controls */}
                        <div className="border border-[#332D2B] bg-[#1C1816] p-5 mb-5">
                          <div className="flex items-center gap-2 mb-3">
                            <Landmark size={14} className="text-[#C8A96A]" />
                            <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#C8A96A] uppercase">Payment Actions</div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              disabled={isUpdating || o.status === "paid"}
                              onClick={(e) => { e.stopPropagation(); setStatus(o, "paid"); }}
                              data-testid={`mark-paid-${o.id}`}
                              className="btn-primary text-[11px] tracking-[0.25em] py-2 px-4 disabled:opacity-40"
                            >
                              <Check size={14} /> Mark Paid
                            </button>
                            <button
                              disabled={isUpdating || o.status === "pending_verification" || o.status === "paid"}
                              onClick={(e) => { e.stopPropagation(); setStatus(o, "pending_verification"); }}
                              data-testid={`mark-verification-${o.id}`}
                              className="font-mono-retro text-[11px] tracking-[0.25em] uppercase border border-[#C8A96A] text-[#C8A96A] hover:bg-[#C8A96A] hover:text-[#0F0C0B] transition-colors py-2 px-4 inline-flex items-center gap-2 disabled:opacity-40"
                            >
                              <Clock size={14} /> Awaiting Bank
                            </button>
                            <button
                              disabled={isUpdating || o.status === "pending_transfer"}
                              onClick={(e) => { e.stopPropagation(); setStatus(o, "pending_transfer"); }}
                              data-testid={`mark-transfer-${o.id}`}
                              className="font-mono-retro text-[11px] tracking-[0.25em] uppercase border border-[#332D2B] text-[#A8A39D] hover:border-[#A8A39D] transition-colors py-2 px-4 inline-flex items-center gap-2 disabled:opacity-40"
                            >
                              Reset to Awaiting
                            </button>
                            <button
                              disabled={isUpdating || o.status === "cancelled"}
                              onClick={(e) => { e.stopPropagation(); if (window.confirm("Cancel this order?")) setStatus(o, "cancelled"); }}
                              data-testid={`mark-cancelled-${o.id}`}
                              className="font-mono-retro text-[11px] tracking-[0.25em] uppercase border border-[#8B4530] text-[#D4936C] hover:bg-[#8B4530] hover:text-[#F2EBDD] transition-colors py-2 px-4 inline-flex items-center gap-2 disabled:opacity-40"
                            >
                              <X size={14} /> Cancel Order
                            </button>
                          </div>
                          {o.fulfilled && (
                            <div className="mt-3 font-mono-retro text-[9px] tracking-[0.25em] text-[#7FB77E] uppercase">
                              ✓ Stock / tickets already reserved for this order
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {/* Shipping details */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#C8A96A] uppercase flex items-center gap-2">
                                <MapPin size={12} /> Ship To
                              </div>
                              {hasShip && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); copyText([o.customer_name, o.shipping_address].filter(Boolean).join("\n"), "Shipping"); }}
                                  data-testid={`copy-ship-${o.id}`}
                                  className="font-mono-retro text-[9px] tracking-[0.25em] text-[#A8A39D] uppercase hover:text-[#C8A96A] inline-flex items-center gap-1"
                                >
                                  <Copy size={11} /> Copy
                                </button>
                              )}
                            </div>
                            {hasShip ? (
                              <div className="text-[#F2EBDD] leading-relaxed">
                                <div className="font-semibold">{o.customer_name}</div>
                                <div className="text-[#A8A39D]">{o.shipping_address}</div>
                                <div className="text-[#756F6B] text-xs mt-1">{o.customer_email}</div>
                              </div>
                            ) : (
                              <div className="text-[#756F6B] text-xs italic">
                                {o.item_type === "raffle_ticket" ? "Raffle ticket — no shipping required" : "No shipping address on file"}
                              </div>
                            )}
                          </div>

                          {/* Kit details */}
                          {o.item_type === "jersey" && (
                            <div>
                              <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#C8A96A] uppercase mb-2">Kit Details</div>
                              <dl className="text-sm space-y-1">
                                <div className="flex gap-3">
                                  <dt className="text-[#756F6B] w-24 shrink-0">Size</dt>
                                  <dd className="text-[#F2EBDD] font-mono-retro tracking-[0.15em]">{o.size || "—"}</dd>
                                </div>
                                <div className="flex gap-3">
                                  <dt className="text-[#756F6B] w-24 shrink-0">Version</dt>
                                  <dd className="text-[#F2EBDD]">{(meta.kit_version || "fan").toUpperCase()}</dd>
                                </div>
                                <div className="flex gap-3">
                                  <dt className="text-[#756F6B] w-24 shrink-0">Sleeve</dt>
                                  <dd className="text-[#F2EBDD]">{(meta.sleeve || "short").toUpperCase()}</dd>
                                </div>
                                <div className="flex gap-3">
                                  <dt className="text-[#756F6B] w-24 shrink-0">Print Name</dt>
                                  <dd className="text-[#F2EBDD]">{meta.print_name ? meta.print_name : <span className="text-[#756F6B]">—</span>}</dd>
                                </div>
                                <div className="flex gap-3">
                                  <dt className="text-[#756F6B] w-24 shrink-0">Print Number</dt>
                                  <dd className="text-[#F2EBDD]">{meta.print_number ? `#${meta.print_number}` : <span className="text-[#756F6B]">—</span>}</dd>
                                </div>
                                <div className="flex gap-3">
                                  <dt className="text-[#756F6B] w-24 shrink-0">Patch</dt>
                                  <dd className="text-[#F2EBDD]">{meta.patch ? String(meta.patch).charAt(0).toUpperCase() + String(meta.patch).slice(1) + " patch" : <span className="text-[#756F6B]">—</span>}</dd>
                                </div>
                                <div className="flex gap-3">
                                  <dt className="text-[#756F6B] w-24 shrink-0">Add-Ons</dt>
                                  <dd className="text-[#F2EBDD]">
                                    {(meta.add_ons && meta.add_ons.length > 0) ? (
                                      <ul className="space-y-1">
                                        {meta.add_ons.map((a, i) => (
                                          <li key={i} className="text-sm">
                                            {a.name} <span className="text-[#C8A96A]">+£{Number(a.price).toFixed(2)}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    ) : <span className="text-[#756F6B]">—</span>}
                                  </dd>
                                </div>
                              </dl>
                            </div>
                          )}

                          {/* Meta */}
                          <div>
                            <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#C8A96A] uppercase mb-2">Order Meta</div>
                            <dl className="text-sm space-y-1">
                              <div className="flex gap-3">
                                <dt className="text-[#756F6B] w-24 shrink-0">Reference</dt>
                                <dd className="text-[#F2EBDD] font-mono-retro text-xs break-all inline-flex items-center gap-2">
                                  {o.order_reference || "—"}
                                  {o.order_reference && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); copyText(o.order_reference, "Reference"); }}
                                      className="text-[#A8A39D] hover:text-[#C8A96A]"
                                      title="Copy reference"
                                    >
                                      <Copy size={11} />
                                    </button>
                                  )}
                                </dd>
                              </div>
                              <div className="flex gap-3">
                                <dt className="text-[#756F6B] w-24 shrink-0">Order ID</dt>
                                <dd className="text-[#F2EBDD] font-mono-retro text-xs break-all">{o.id}</dd>
                              </div>
                              <div className="flex gap-3">
                                <dt className="text-[#756F6B] w-24 shrink-0">Method</dt>
                                <dd className="text-[#F2EBDD] uppercase">{(o.payment_method || "bank_transfer").replace("_", " ")}</dd>
                              </div>
                              <div className="flex gap-3">
                                <dt className="text-[#756F6B] w-24 shrink-0">Currency</dt>
                                <dd className="text-[#F2EBDD] uppercase">{o.currency || "GBP"}</dd>
                              </div>
                              <div className="flex gap-3">
                                <dt className="text-[#756F6B] w-24 shrink-0">Updated</dt>
                                <dd className="text-[#A8A39D] text-xs">{o.updated_at ? new Date(o.updated_at).toLocaleString() : "—"}</dd>
                              </div>
                            </dl>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
